import React, { useCallback, useMemo } from "react";
import { Actionable, Card, Carousel, Image, Stack, Text } from "@bookingcom/bui-react";
import useFormatPrice from "../../../hooks/useFormatPrice.js";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useFormatDateTime } from "@bookingcom/flights-core";
import { useCreateFlexUrl } from "./useCreateFlexUrl.js";
import styles from "./FlexibleDateBestPriceInner.module.css";
import useFlexPriceDiffTracker from "../../../hooks/useFlexPriceDiffTracker.js";
const FlexibleDateBestPriceInner = ({ dates, cheaperCount, isOneWay, currentBestPrice, carouselSize }) => {
    const i18n = useI18n();
    const { formats } = useFormatDateTime(i18n);
    const { formatPrice } = useFormatPrice();
    const { createUrl } = useCreateFlexUrl();
    const { setOriginPrice } = useFlexPriceDiffTracker();
    const cardDate = useCallback((departureDate, returnDate) => {
        if (isOneWay) {
            return formats.flightDate(departureDate);
        }
        return i18n.trans(t("flights_sr_flex_dates_alt_date_rt_multi", {
            variables: {
                begin_date: formats.flightDate(departureDate),
                end_date: returnDate ? formats.flightDate(returnDate) : ""
            }
        }));
    }, [formats, i18n, isOneWay]);
    return (React.createElement(Carousel, { size: carouselSize, title: React.createElement(Title, { cheaperCount: cheaperCount }), nextButtonAriaLabel: i18n.trans(t("a11y_flights_sr_flex_dates_carousel_forward")), previousButtonAriaLabel: i18n.trans(t("a11y_flights_sr_flex_dates_carousel_back")), ariaLabel: i18n.trans(t("a11y_flights_sr_flex_dates_carousel_entry")) }, dates.map((date) => (React.createElement(Actionable, { key: date.departureDate, href: createUrl(date), className: styles.anchor, onClick: () => {
            setOriginPrice(currentBestPrice.units);
        } },
        React.createElement(Card, { variant: "elevated", className: styles.card },
            React.createElement(Text, { color: "neutral", align: "center", variant: "body_2" }, cardDate(date.departureDate, date.returnDate)),
            React.createElement(Text, { align: "center", variant: "strong_2", color: getPriceTextColor({ date, hasCheaperPrice: cheaperCount > 0 }), attributes: {
                    "aria-label": date.isCheapest
                        ? i18n.trans(t("flights_a11y_green_text_sr_lowest_price"))
                        : date.isExpensive
                            ? i18n.trans(t("flights_a11y_red_text_sr_lowest_price"))
                            : undefined
                } }, formatPrice(date.priceRounded, { decimalPlaces: 0 }))))))));
};
const Title = ({ cheaperCount }) => {
    const i18n = useI18n();
    const title = useMemo(() => {
        if (cheaperCount === 0) {
            return i18n.trans(t("flights_sr_flex_dates_best_price"));
        }
        if (cheaperCount === 1) {
            return i18n.trans(t("flights_sr_flex_date_better_price_one"));
        }
        return i18n.trans(t("flights_sr_flex_date_better_prices_multi"));
    }, [cheaperCount, i18n]);
    return (React.createElement(Stack, { direction: "row", alignItems: "center" },
        React.createElement(Image, { width: "40px", height: "40px", asset: {
                setName: "illustrations-traveller",
                assetName: "FlightsSearch"
            } }),
        React.createElement(Stack.Item, { shrink: true, grow: true },
            React.createElement(Stack, { gap: 0 },
                React.createElement(Text, { variant: "strong_1", tagName: "h2" }, title),
                React.createElement(Text, { variant: "body_2" }, i18n.trans(t("flights_sr_flexi_search_subtitle_latest_prices")))))));
};
const getPriceTextColor = ({ date, hasCheaperPrice }) => {
    date.isCheapest ? "constructive" : date.isExpensive ? "destructive" : "neutral";
    if (hasCheaperPrice && date.isCheapest) {
        return "constructive";
    }
    if (date.isExpensive) {
        return "destructive";
    }
    return "neutral";
};
export default FlexibleDateBestPriceInner;
